import React, { useContext, useEffect, useState, useRef } from 'react';
import { ProductContext } from '../ProductProvider';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { useSnackbar } from 'notistack';
import { Dropdown } from 'react-bootstrap';
import VJLOGO from '../assets/logo/VJLOGO.png';
import './Navbar.css';

export default function Navbar() {
  const { loggedUser, setLoggedUser, myProfileImage } = useContext(ProductContext);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  const [showProfileFallback, setShowProfileFallback] = useState(false);
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const navRef = useRef(null);

  useEffect(() => {
    setShowProfileFallback(false);
  }, [myProfileImage, loggedUser.name]);

  // Close mobile nav on route change
  useEffect(() => {
    setIsNavCollapsed(true);
  }, [location.pathname]);

  const avatarLetter = (loggedUser.name || loggedUser.username || 'G').trim().charAt(0).toUpperCase();

  const handleSignOut = async (event) => {
    if (event) event.preventDefault();
    try {
      await signOut();
      setLoggedUser({
        ...loggedUser,
        name: '',
        email: '',
        DOB: '',
        profile: '',
        provinceName: '',
        website: '',
        isAuthenticated: false,
        isAdmin: false
      });
      enqueueSnackbar('Signed out successfully', { variant: 'info' });
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
      enqueueSnackbar('Error during sign out: ' + error.message, { variant: 'warning' });
      navigate('/');
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="vj-navbar-header" ref={navRef}>
      <div className="container-fluid px-3">
        <nav className="navbar navbar-expand-lg vj-navbar-container">
          {/* Brand Logo & Title */}
          <Link to="/" className="vj-nav-brand">
            <img src={VJLOGO} className="vj-nav-logo" alt="Flynn Meagher Library Logo" />
            <div className="d-flex flex-column">
              <span className="vj-nav-brand-main">
                FLYNN - MEAGHER <span style={{ color: '#7ee8fa', fontWeight: 700 }}>Library</span>
              </span>
              <span className="vj-nav-brand-sub d-none d-sm-inline">
                Vidyajyoti College of Theology
              </span>
            </div>
          </Link>

          {/* Mobile Hamburger Toggle Button */}
          <button
            className="vj-navbar-toggler d-lg-none ms-auto me-2"
            type="button"
            onClick={() => setIsNavCollapsed(!isNavCollapsed)}
            aria-label="Toggle navigation menu"
          >
            <i className={`bi ${isNavCollapsed ? 'bi-list' : 'bi-x-lg'}`}></i>
          </button>

          {/* Collapsible Navigation Menu */}
          <div className={`collapse navbar-collapse ${!isNavCollapsed ? 'show vj-mobile-menu' : ''}`} id="navbarSupportedContent">
            <ul className="navbar-nav ms-auto align-items-lg-center">
              {/* Home */}
              <li className="nav-item">
                <Link
                  to="/"
                  className={`vj-nav-link ${isActive('/') ? 'active' : ''}`}
                  onClick={() => setIsNavCollapsed(true)}
                >
                  <i className="bi bi-house-door"></i> Home
                </Link>
              </li>

              {/* About Us */}
              <li className="nav-item">
                <Link
                  to="/aboutus"
                  className={`vj-nav-link ${isActive('/aboutus') ? 'active' : ''}`}
                  onClick={() => setIsNavCollapsed(true)}
                >
                  <i className="bi bi-info-circle"></i> About
                </Link>
              </li>

              {/* Search Catalogue */}
              <li className="nav-item">
                <Link
                  to="/search"
                  className={`vj-nav-link ${isActive('/search') ? 'active' : ''}`}
                  onClick={() => setIsNavCollapsed(true)}
                >
                  <i className="bi bi-search"></i> Search
                </Link>
              </li>

              {/* Admin / Librarian Management Dropdown */}
              {loggedUser.isAdmin && (
                <li className="nav-item">
                  <Dropdown align="end">
                    <Dropdown.Toggle
                      as="button"
                      className={`vj-nav-link border-0 bg-transparent ${isActive('/issuereturnbooks') ||
                        isActive('/managebooks') ||
                        isActive('/managejournal') ||
                        isActive('/barcode') ||
                        isActive('/reports')
                        ? 'active'
                        : ''
                        }`}
                    >
                      <i className="bi bi-shield-lock me-1"></i> Library Admin
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="vj-dropdown-menu shadow">
                      <Dropdown.Item
                        as={Link}
                        to="/issuereturnbooks"
                        className="vj-dropdown-item"
                        onClick={() => setIsNavCollapsed(true)}
                      >
                        <i className="bi bi-arrow-left-right"></i> Issue / Return Desk
                      </Dropdown.Item>

                      <Dropdown.Item
                        as={Link}
                        to="/managebooks"
                        className="vj-dropdown-item"
                        onClick={() => setIsNavCollapsed(true)}
                      >
                        <i className="bi bi-book"></i> Manage Books Catalogue
                      </Dropdown.Item>

                      <Dropdown.Item
                        as={Link}
                        to="/managejournal"
                        className="vj-dropdown-item"
                        onClick={() => setIsNavCollapsed(true)}
                      >
                        <i className="bi bi-journal-album"></i> Manage Journals & Issues
                      </Dropdown.Item>

                      <Dropdown.Divider className="my-1" />

                      <Dropdown.Item
                        as={Link}
                        to="/barcode"
                        className="vj-dropdown-item"
                        onClick={() => setIsNavCollapsed(true)}
                      >
                        <i className="bi bi-upc-scan"></i> Barcode Print Studio
                      </Dropdown.Item>

                      <Dropdown.Item
                        as={Link}
                        to="/reports"
                        className="vj-dropdown-item"
                        onClick={() => setIsNavCollapsed(true)}
                      >
                        <i className="bi bi-bar-chart-line"></i> Reports
                      </Dropdown.Item>

                      <Dropdown.Divider className="my-1" />

                      <Dropdown.Item
                        as={Link}
                        to="/help"
                        className="vj-dropdown-item"
                        onClick={() => setIsNavCollapsed(true)}
                      >
                        <i className="bi bi-journal-check text-primary"></i> Staff SOP & Help Guide
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </li>
              )}

              {/* Help & Guides */}
              <li className="nav-item">
                <Link
                  to="/help"
                  className={`vj-nav-link ${isActive('/help') ? 'active' : ''}`}
                  onClick={() => setIsNavCollapsed(true)}
                >
                  <i className="bi bi-question-circle"></i> Help & Guides
                </Link>
              </li>

              {/* User Authentication & Profile */}
              {!loggedUser.isAuthenticated ? (
                <li className="nav-item ms-lg-2 mt-2 mt-lg-0">
                  <Link
                    to="/login"
                    className="vj-signin-btn"
                    onClick={() => setIsNavCollapsed(true)}
                  >
                    <i className="bi bi-box-arrow-in-right"></i> Sign In
                  </Link>
                </li>
              ) : (
                <li className="nav-item ms-lg-2 mt-2 mt-lg-0">
                  <Dropdown align="end">
                    <Dropdown.Toggle as="div" className="vj-user-btn">
                      {showProfileFallback ? (
                        <div className="vj-user-avatar-letter">
                          {avatarLetter}
                        </div>
                      ) : (
                        <img
                          className="vj-user-avatar"
                          src={myProfileImage}
                          alt="User avatar"
                          onError={() => setShowProfileFallback(true)}
                        />
                      )}
                      <span>{loggedUser.name || loggedUser.username}</span>
                      {loggedUser.profile && (
                        <span
                          className="badge ms-1"
                          style={{
                            background: 'rgba(255, 255, 255, 0.25)',
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            padding: '2px 6px'
                          }}
                        >
                          {loggedUser.profile}
                        </span>
                      )}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="vj-dropdown-menu shadow">
                      <div className="px-3 py-1 border-bottom mb-1">
                        <div className="fw-bold text-dark" style={{ fontSize: '0.82rem' }}>{loggedUser.name}</div>
                        <div className="text-muted text-truncate" style={{ maxWidth: '180px', fontSize: '0.75rem' }}>
                          {loggedUser.email || loggedUser.profile}
                        </div>
                      </div>

                      <Dropdown.Item
                        as={Link}
                        to="/myprofile"
                        className="vj-dropdown-item"
                        onClick={() => setIsNavCollapsed(true)}
                      >
                        <i className="bi bi-person-circle"></i> My Profile
                      </Dropdown.Item>

                      <Dropdown.Item
                        as={Link}
                        to="/mybooks"
                        className="vj-dropdown-item"
                        onClick={() => setIsNavCollapsed(true)}
                      >
                        <i className="bi bi-journal-bookmark"></i> My Borrowed Books
                      </Dropdown.Item>

                      <Dropdown.Divider className="my-1" />

                      <Dropdown.Item
                        as="button"
                        onClick={handleSignOut}
                        className="vj-dropdown-item text-danger"
                      >
                        <i className="bi bi-box-arrow-right"></i> Sign Out
                      </Dropdown.Item>

                      <Dropdown.Divider className="my-1" />

                      <Dropdown.Item
                        className="vj-dropdown-item text-muted"
                        style={{ fontSize: '0.75rem', cursor: 'default' }}
                      >
                        <i className="bi bi-tag"></i> Version 1.0.13
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </li>
              )}
            </ul>
          </div>
        </nav>
      </div>
    </header>
  );
}
